import { get } from '@ember/object';
import GroupCondition from 'cio/app-models/conditions/group';
import InvalidCondition from 'cio/app-models/conditions/invalid';
import attribute from './deserialize/attribute';
import deviceAttribute from './deserialize/device-attribute';
import email from './deserialize/email';
import event from './deserialize/event';
import form from './deserialize/form';
import pageview from './deserialize/pageview';
import screenview from './deserialize/screenview';
import action from './deserialize/action';
import relationship, { isRelationshipGroupedCondition } from './deserialize/relationship';
import { FILTERS as EMAIL_FILTERS } from 'cio/utils/email-filters';
import { FILTERS as ACTION_FILTERS } from 'cio/utils/action-filters';
import {
  SerializedGroupConditionAnd,
  SerializedGroupConditionOr,
  SerializedAttributeCondition,
  SerializedDeviceAttributeCondition,
  SerializedEventCondition,
  SerializedFormCondition,
  SerializedPageviewCondition,
  SerializedScreenviewCondition,
  SerializedEmailCondition,
  SerializedActionCondition,
  SerializedConditions,
} from 'cio/types/conditions/condition';
import AbstractCondition from 'cio/app-models/conditions/abstract';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import EventCondition from 'cio/app-models/conditions/event';
import FormCondition from 'cio/app-models/conditions/form';
import PageviewCondition from 'cio/app-models/conditions/pageview';
import ScreenviewCondition from 'cio/app-models/conditions/screenview';
import EmailCondition from 'cio/app-models/conditions/email';
import ActionCondition from 'cio/app-models/conditions/action';
import ObjectAttributeCondition from 'cio/app-models/conditions/object-attribute';
import RelationshipCondition from 'cio/app-models/conditions/relationship';
import {
  SerializedLegacyAttributeFilterOr,
  SerializedLegacyAttributeFilterAnd,
  SerializedLegacyAttributeFilter,
  SerializedLegacyAttributeFilters,
  SerializedLegacyFilter,
  SerializedLegacySegmentNegatableFilter,
  SerializedLegacyMessageFilter,
  SerializedLegacyNewsletterRecipientFilter,
  SerializedLegacyAttributeCompareFilter,
} from 'cio/types/conditions/legacy-filter';
import { ConditionType } from 'cio/utils/condition-types';

export const isGroupOrCondition = (
  filter: SerializedLegacyAttributeFilters | Record<string, never>,
): filter is SerializedLegacyAttributeFilterOr => !!(filter as SerializedLegacyAttributeFilterOr).or;
export const isGroupAndCondition = (
  filter: SerializedLegacyAttributeFilters | Record<string, never>,
): filter is SerializedLegacyAttributeFilterAnd => !!(filter as SerializedLegacyAttributeFilterAnd).and;
export const isSimpleCondition = (
  filter: SerializedLegacyAttributeFilters | Record<string, never>,
): filter is SerializedLegacyAttributeFilter => !!(filter as SerializedLegacyAttributeFilter).operator;
export const isAttributeCondition = (condition: SerializedLegacyFilter): condition is SerializedLegacyAttributeFilter =>
  condition.type === 'attribute';
export const isAttributeCompareCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeCompareFilter => condition.type === 'attribute_compare';
export const isForeignEventAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter => condition.type === 'foreign_event_attribute';
export const isEventAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter => condition.type === 'event_attribute';
export const isObjectAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter => condition.type === 'object_attribute';
export const isRelationshipAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter => condition.type === ConditionType.RelationshipAttribute;
export const isCollectionAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter => condition.type === 'collection_attribute';
export const isDeviceAttributeCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyAttributeFilter =>
  condition.type === 'device_change' || condition.type === 'device_attribute';
export const isSerializedSegmentNegatableCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacySegmentNegatableFilter => condition.type === 'segments_negatable';
export const isMessageCondition = (condition: SerializedLegacyFilter): condition is SerializedLegacyMessageFilter =>
  condition.type === 'message';
export const isSerializedNewsletterRecipientCondition = (
  condition: SerializedLegacyFilter,
): condition is SerializedLegacyNewsletterRecipientFilter => condition.type === 'newsletter_recipients';

function unpackGroupCondition(
  json: SerializedConditions,
): { operator: 'and' | 'or'; children: SerializedConditions[] } | null {
  let { and } = json as SerializedGroupConditionAnd;
  let { or } = json as SerializedGroupConditionOr;

  if (!and && !or) {
    return null;
  }

  return {
    operator: and ? 'and' : 'or',
    children: and || or,
  };
}

export default function deserialize(
  json: SerializedConditions,
):
  | AttributeCondition
  | DeviceAttributeCondition
  | EventCondition
  | FormCondition
  | PageviewCondition
  | ScreenviewCondition
  | EmailCondition
  | ActionCondition
  | GroupCondition
  | RelationshipCondition
  | InvalidCondition
  | ObjectAttributeCondition
  | null {
  if (!json) {
    return null;
  }

  // Relationship structure is kinda group condition, thus need to check before "group"
  if (isRelationshipGroupedCondition(json)) {
    return relationship(json as SerializedGroupConditionAnd);
  }

  let group = unpackGroupCondition(json);
  if (group) {
    return GroupCondition.create({
      operator: group.operator,
      children: group.children.map(deserialize) as AbstractCondition[],
    });
  }

  // @ts-ignore: accessing nested property
  let type: string | undefined = get(json, 'event.ui_type') || get(json, 'event.type');

  if (type === 'attribute_change') {
    return attribute(json as SerializedAttributeCondition);
  } else if (type === 'device_change') {
    return deviceAttribute(json as SerializedDeviceAttributeCondition);
  } else if (type === 'event') {
    return event(json as SerializedEventCondition);
  } else if (type === 'form') {
    return form(json as SerializedFormCondition);
  } else if (type === 'page') {
    return pageview(json as SerializedPageviewCondition);
  } else if (type === 'screen') {
    return screenview(json as SerializedScreenviewCondition);
  } else if (type != null && EMAIL_FILTERS.includes(type)) {
    return email(json as SerializedEmailCondition);
  } else if (type != null && ACTION_FILTERS.includes(type)) {
    return action(json as SerializedActionCondition);
  }

  return InvalidCondition.create({ json });
}
